import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { customersAPI, cardsAPI } from '../lib/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Plus, Users, Loader2, Search, MoreVertical, Edit2, Trash2 } from 'lucide-react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';

const PAGE = 50;

const CustomersPage = () => {
  const [customers, setCustomers] = useState([]);
  const [total, setTotal] = useState(0);
  const [loadingMore, setLoadingMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [query, setQuery] = useState('');
  const [deleteId, setDeleteId] = useState(null);
  const [cards, setCards] = useState([]);
  const navigate = useNavigate();

  // Wait for the merchant to stop typing before searching.
  useEffect(() => {
    const t = setTimeout(() => setQuery(search.trim()), 250);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    cardsAPI.list().then(({ data }) => setCards(Array.isArray(data) ? data : (data.cards || []))).catch(() => {});
  }, []);

  const progressOf = (customer) => (customer.card_balances || [])
    .map((b) => {
      const card = cards.find((c) => c.id === b.card_id);
      if (!card) return null;
      const label = card.card_type === 'stamps'
        ? `${b.points}/${card.total_stamps || 10}`
        : `${b.points} pts`;
      return { id: card.id, name: card.name, color: card.primary_color || '#64748B', label };
    })
    .filter(Boolean);

  const renderProgress = (customer) => {
    const items = progressOf(customer);
    if (items.length === 0) return <span className="text-sm text-slate-400 whitespace-nowrap">Aucune carte</span>;
    return (
      <div className="flex flex-wrap gap-1.5 justify-end">
        {items.map((it) => (
          <span key={it.id} title={it.name}
            className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-slate-100 text-sm font-semibold text-slate-800 tabular-nums whitespace-nowrap">
            <span className="w-2 h-2 rounded-full" style={{ backgroundColor: it.color }} />
            {it.label}
          </span>
        ))}
      </div>
    );
  };

  const fetchCustomers = useCallback(async () => {
    try {
      const { data } = await customersAPI.list({ search: query, limit: PAGE });
      setCustomers(data.customers);
      setTotal(data.total);
    } catch (error) {
      console.error('Failed to fetch customers:', error);
    } finally {
      setLoading(false);
    }
  }, [query]);

  useEffect(() => {
    fetchCustomers();
  }, [fetchCustomers]);

  const loadMore = async () => {
    setLoadingMore(true);
    try {
      const { data } = await customersAPI.list({ search: query, limit: PAGE, skip: customers.length });
      setCustomers((prev) => [...prev, ...data.customers.filter((c) => !prev.some((p) => p.id === c.id))]);
      setTotal(data.total);
    } catch (error) {
      console.error('Failed to load more customers:', error);
    } finally {
      setLoadingMore(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await customersAPI.delete(deleteId);
      setCustomers(customers.filter(c => c.id !== deleteId));
      setTotal(total - 1);
    } catch (error) {
      console.error('Failed to delete customer:', error);
    }
    setDeleteId(null);
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900" style={{ fontFamily: 'Cabinet Grotesk' }}>
            Clients
          </h1>
          <p className="text-slate-600 mt-1">{total} client{total > 1 ? 's' : ''} au total</p>
        </div>
        <Link to="/dashboard/customers/new">
          <Button className="bg-blue-600 hover:bg-blue-700" data-testid="add-customer-btn">
            <Plus className="w-4 h-4 mr-2" /> Ajouter un client
          </Button>
        </Link>
      </div>

      {/* Search */}
      <Card className="border-slate-200">
        <CardContent className="p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Nom, email ou téléphone"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10"
              data-testid="customer-search-input"
            />
          </div>
        </CardContent>
      </Card>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        </div>
      ) : customers.length > 0 ? (
        <>
        {/* Phone: one row per customer, tap to open */}
        <Card className="border-slate-200 md:hidden">
          <ul className="divide-y divide-slate-100">
            {customers.map((customer) => (
              <li key={customer.id}>
                <button
                  type="button"
                  onClick={() => navigate(`/dashboard/customers/${customer.id}`)}
                  className="w-full flex items-center gap-3 px-4 py-3.5 text-left active:bg-slate-50"
                  data-testid={`customer-item-${customer.id}`}
                >
                  <div className="w-10 h-10 bg-slate-100 rounded-full flex items-center justify-center shrink-0">
                    <span className="text-base font-semibold text-slate-600">{customer.name?.charAt(0)?.toUpperCase() || '?'}</span>
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="font-semibold text-slate-900 truncate">{customer.name}</p>
                    <p className="text-sm text-slate-500 truncate">{customer.phone || customer.email || 'Sans contact'}</p>
                  </div>
                  {renderProgress(customer)}
                </button>
              </li>
            ))}
          </ul>
        </Card>

        <Card className="border-slate-200 hidden md:block">
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nom</TableHead>
                  <TableHead>Contact</TableHead>
                  <TableHead className="text-right">Carte</TableHead>
                  <TableHead className="text-right">Visites</TableHead>
                  <TableHead className="text-right">Inscrit le</TableHead>
                  <TableHead className="w-10"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {customers.map((customer) => (
                  <TableRow key={customer.id} className="table-row-hover cursor-pointer" data-testid={`customer-row-${customer.id}`}>
                    <TableCell onClick={() => navigate(`/dashboard/customers/${customer.id}`)}>
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-slate-100 rounded-full flex items-center justify-center">
                          <span className="text-sm font-medium text-slate-600">
                            {customer.name?.charAt(0) || '?'}
                          </span>
                        </div>
                        <span className="font-medium text-slate-900">{customer.name}</span>
                      </div>
                    </TableCell>
                    <TableCell onClick={() => navigate(`/dashboard/customers/${customer.id}`)}>
                      <div className="text-sm">
                        {customer.email && <p className="text-slate-600">{customer.email}</p>}
                        {customer.phone && <p className="text-slate-500">{customer.phone}</p>}
                      </div>
                    </TableCell>
                    <TableCell className="text-right" onClick={() => navigate(`/dashboard/customers/${customer.id}`)}>
                      {renderProgress(customer)}
                    </TableCell>
                    <TableCell className="text-right" onClick={() => navigate(`/dashboard/customers/${customer.id}`)}>
                      {customer.total_visits}
                    </TableCell>
                    <TableCell className="text-right text-slate-500" onClick={() => navigate(`/dashboard/customers/${customer.id}`)}>
                      {new Date(customer.created_at).toLocaleDateString('fr-FR')}
                    </TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => navigate(`/dashboard/customers/${customer.id}`)}>
                            <Edit2 className="w-4 h-4 mr-2" /> Voir / Modifier
                          </DropdownMenuItem>
                          <DropdownMenuItem 
                            onClick={() => setDeleteId(customer.id)}
                            className="text-red-600"
                          >
                            <Trash2 className="w-4 h-4 mr-2" /> Supprimer
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {customers.length < total && (
          <div className="flex justify-center">
            <Button variant="outline" onClick={loadMore} disabled={loadingMore} data-testid="load-more-customers">
              {loadingMore && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Afficher plus ({total - customers.length} restant{total - customers.length > 1 ? 's' : ''})
            </Button>
          </div>
        )}
        </>
      ) : (
        <Card className="border-slate-200">
          <CardContent className="flex flex-col items-center justify-center py-16">
            <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mb-4">
              <Users className="w-8 h-8 text-slate-400" />
            </div>
            <h3 className="text-lg font-semibold text-slate-900 mb-2">
              {search ? 'Aucun client trouvé' : 'Aucun client'}
            </h3>
            <p className="text-slate-500 text-center mb-6 max-w-sm">
              {search ? 'Essayez un autre terme de recherche' : 'Ajoutez votre premier client pour démarrer votre programme de fidélité'}
            </p>
            {!search && (
              <Link to="/dashboard/customers/new">
                <Button className="bg-blue-600 hover:bg-blue-700">
                  <Plus className="w-4 h-4 mr-2" /> Ajouter mon premier client
                </Button>
              </Link>
            )}
          </CardContent>
        </Card>
      )}

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer ce client ?</AlertDialogTitle>
            <AlertDialogDescription>
              Cette action est irréversible. Le client, ses tampons ou points et son historique seront définitivement supprimés.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default CustomersPage;
